import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const TrainingDevelopmentModule = () => {
  const [activeSection, setActiveSection] = useState("learning-management");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleTraining = () => navigate('/access-recruit');

  const sectionContent = {
    "learning-management": {
      title: "Learning Management System",
      description: "Deliver comprehensive training programs through our integrated learning management platform. Create, manage, and distribute online courses, instructor-led training, and blended learning experiences. Track course enrollment, completion rates, and learning progress across the organization. Implement learning paths, certifications, and competency-based training programs. Support multiple content formats including videos, documents, SCORM packages, and interactive modules for diverse learning preferences and requirements."
    },
    "course-development": {
      title: "Course Development",
      description: "Create engaging training content with built-in authoring tools and content management capabilities. Develop interactive courses, assessments, and learning materials without technical expertise. Import existing training content, convert legacy materials, and create new courses from templates. Implement multimedia elements, interactive quizzes, and practical exercises. Maintain version control, content libraries, and reusable learning objects. Ensure consistent training quality and branding across all learning materials."
    },
    "skill-development": {
      title: "Skill Development",
      description: "Align training programs with organizational skills requirements through systematic competency mapping and gap analysis. Define competency frameworks, skill matrices, and proficiency levels for different roles. Assess current skill levels, identify development gaps, and recommend targeted training interventions. Track skill progression, competency achievement, and development plan completion. Link skill development to performance management, career progression, and organizational capability building."
    },
    "training-delivery": {
      title: "Training Delivery & Scheduling",
      description: "Coordinate and deliver training programs through multiple delivery methods and scheduling tools. Manage instructor-led sessions, virtual classrooms, webinars, and self-paced online learning. Schedule training sessions, manage registrations, and handle waitlists. Coordinate training logistics, resources, and facilities. Track attendance, participation, and completion for all training activities. Support hybrid learning models that combine in-person and virtual delivery methods."
    },
    "training-analytics": {
      title: "Training Analytics",
      description: "Measure training effectiveness, ROI, and business impact through comprehensive analytics and reporting tools. Track completion rates, assessment scores, and learner satisfaction. Analyze training effectiveness on performance improvement, skill development, and behavioral change. Calculate training ROI, cost-effectiveness, and business impact metrics. Generate compliance reports, training completion certificates, and development progress reports for managers and leadership."
    }
  };

  const sections = [
    { id: "learning-management", label: "LMS" },
    { id: "course-development", label: "COURSE DEV" },
    { id: "skill-development", label: "SKILLS" },
    { id: "training-delivery", label: "DELIVERY" },
    { id: "training-analytics", label: "ANALYTICS" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Learning Management System */}
        <section
          ref={(el) => (sectionsRef.current["learning-management"] = el)}
          data-section="learning-management"
          className={`resto-section ${visibleSections.has("learning-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Learning Platform</div>
            <h2 className="resto-section-title">
              {sectionContent["learning-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["learning-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTraining}>Access LMS</button>
              <button className="resto-secondary-btn" onClick={handleTraining}>Browse Courses</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/tro1.png" className="resto-real-image" alt="Learning Management" />
          </div>
        </section>

        {/* 2. Course Development */}
        <section
          ref={(el) => (sectionsRef.current["course-development"] = el)}
          data-section="course-development"
          className={`resto-section resto-reverse ${visibleSections.has("course-development") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Course Development</div>
            <h2 className="resto-section-title">
              {sectionContent["course-development"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["course-development"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTraining}>Create Course</button>
              <button className="resto-secondary-btn" onClick={handleTraining}>Content Library</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/tro2.png" className="resto-real-image" alt="Course Development" />
          </div>
        </section>

        <CTA/>

        {/* 3. Skill Development */}
        <section
          ref={(el) => (sectionsRef.current["skill-development"] = el)}
          data-section="skill-development"
          className={`resto-section ${visibleSections.has("skill-development") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Skills</div>
            <h2 className="resto-section-title">
              {sectionContent["skill-development"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["skill-development"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTraining}>Map Competencies</button>
              <button className="resto-secondary-btn" onClick={handleTraining}>Assess Skills</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/tro3.png" className="resto-real-image" alt="Skill Development" />
          </div>
        </section>

        {/* 4. Training Delivery */}
        <section
          ref={(el) => (sectionsRef.current["training-delivery"] = el)}
          data-section="training-delivery"
          className={`resto-section resto-reverse ${visibleSections.has("training-delivery") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Delivery</div>
            <h2 className="resto-section-title">
              {sectionContent["training-delivery"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["training-delivery"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTraining}>Schedule Training</button>
              <button className="resto-secondary-btn" onClick={handleTraining}>Manage Sessions</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/tro4.png" className="resto-real-image" alt="Training Delivery" />
          </div>
        </section>

        {/* 5. Training Analytics */}
        <section
          ref={(el) => (sectionsRef.current["training-analytics"] = el)}
          data-section="training-analytics"
          className={`resto-section ${visibleSections.has("training-analytics") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Analytics</div>
            <h2 className="resto-section-title">
              {sectionContent["training-analytics"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["training-analytics"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleTraining}>View Analytics</button>
              <button className="resto-secondary-btn" onClick={handleTraining}>Measure Impact</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/tro5.png" className="resto-real-image" alt="Training Analytics" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default TrainingDevelopmentModule;